/**
 * Admin Orca Task RPC definitions
 *
 * Admin endpoints for orca task management — list, find, kill, reset,
 * tombstone (INF-526).
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { OrcaTask, OrcaTaskGate, OrcaTaskPhase } from "@fm/db/models/OrcaTask";
import { OrcaTaskEvent } from "@fm/db/models/OrcaTaskEvent";
import { OrcaTaskTokenUsage } from "@fm/db/models/OrcaTaskTokenUsage";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized } from "../errors.ts";

// ============================================================================
// Error Schemas
// ============================================================================

/**
 * Error for agent runner operations (kill)
 * @since 1.0.0
 */
export class OrcaAgentError extends Schema.TaggedError<OrcaAgentError>()(
  "OrcaAgentError",
  {
    message: Schema.String,
  },
) {}

/**
 * INF-754: refusal raised by `admin.orcaTask.retry` when the task's current
 * phase is not rewindable. Terminal phases (PASSED / SUPERSEDED / CANCELED_* /
 * HOLD) are refused outright; the non-terminal `BUILDING` / `MERGING` phases are
 * refused unless the caller passes `force`. Carries the offending `phase` and
 * the `force` flag the caller sent so the operator sees exactly why the rewind
 * was declined.
 *
 * @since 1.0.0
 */
export class OrcaTaskPhaseNotRewindableError
  extends Schema.TaggedError<OrcaTaskPhaseNotRewindableError>()("OrcaTaskPhaseNotRewindableError", {
    id: Schema.String,
    phase: OrcaTaskPhase,
    force: Schema.Boolean,
    message: Schema.String,
  })
{}

// ============================================================================
// Response Schemas
// ============================================================================

/**
 * OL-13: the kanban-card projection `admin.orcaTask.list` returns — the full
 * `OrcaTask` shape (with `description` / `failureContext` / `attemptHistory` /
 * `history` stubbed at the SQL level by `findAllForKanban`) plus `runnerName`,
 * the operator-facing host label of the runner the task is currently placed on.
 * `runnerName` is derived from the newest `runner_assigned` history entry SQL-
 * side, so the card shows where the container runs without TOAST-decompressing
 * the forensic history. NULL when the task was never dispatched to a runner.
 *
 * @since 1.0.0
 */
export const OrcaKanbanTaskRow = OrcaTask.json.pipe(
  Schema.extend(Schema.Struct({ runnerName: Schema.NullOr(Schema.String) })),
);
export type OrcaKanbanTaskRow = Schema.Schema.Type<typeof OrcaKanbanTaskRow>;

// ============================================================================
// Request Schemas
// ============================================================================

/**
 * Input schema for finding a single orca task by id
 * @since 1.0.0
 */
export const OrcaTaskFindOneInput = Schema.Struct({
  id: Schema.String,
});

/**
 * Input schema for killing an orca task's container
 * @since 1.0.0
 */
export const OrcaTaskKillInput = Schema.Struct({
  id: Schema.String,
  containerId: Schema.String,
});

/**
 * Input schema for resetting an orca task back to BUILDING phase
 * @since 1.0.0
 */
export const OrcaTaskResetInput = Schema.Struct({
  id: Schema.String,
});

/**
 * INF-754: input for `admin.orcaTask.retry` — the codified CI-gate rewind that
 * replaces the hand-SQL failed-task recovery run six times in prod on
 * 2026-08-28. The target is always `CI_CHECKING` / `gate='ci'`.
 *
 * - `attempt` — restore the attempt budget to this value; omitted leaves the
 *   current value unchanged.
 * - `resyncDescription` (default true) — copy the Linear-cache `description`
 *   over the task snapshot when it differs (gates judge the snapshot, not live
 *   Linear).
 * - `killContainer` — when set, kill this container id BEFORE rewinding.
 * - `force` (default false) — widen the phase allowlist to the remaining
 *   NON-terminal phases (`BUILDING`, `MERGING`). Terminal phases are refused
 *   regardless.
 *
 * @since 1.0.0
 */
export const OrcaTaskRetryInput = Schema.Struct({
  id: Schema.String,
  attempt: Schema.optional(Schema.Number),
  resyncDescription: Schema.optionalWith(Schema.Boolean, { default: () => true }),
  killContainer: Schema.optional(Schema.String),
  force: Schema.optionalWith(Schema.Boolean, { default: () => false }),
});
export type OrcaTaskRetryInput = Schema.Schema.Type<typeof OrcaTaskRetryInput>;

/**
 * A before/after snapshot of the phase-defining columns a retry rewinds.
 *
 * @since 1.0.0
 */
export const OrcaTaskRetrySummary = Schema.Struct({
  phase: OrcaTaskPhase,
  gate: Schema.NullOr(OrcaTaskGate),
  attempt: Schema.Number,
});
export type OrcaTaskRetrySummary = Schema.Schema.Type<typeof OrcaTaskRetrySummary>;

/**
 * Result of `admin.orcaTask.retry`: the row summary before and after the
 * rewind, plus whether the description snapshot was resynced from the Linear
 * cache.
 *
 * @since 1.0.0
 */
export const OrcaTaskRetryResult = Schema.Struct({
  before: OrcaTaskRetrySummary,
  after: OrcaTaskRetrySummary,
  descriptionResynced: Schema.Boolean,
});
export type OrcaTaskRetryResult = Schema.Schema.Type<typeof OrcaTaskRetryResult>;

/**
 * Terminal human-tombstone phase an operator may apply via the tombstone
 * endpoint. Restricted to the INF-526 phases (SUPERSEDED / HOLD) — the
 * CANCELED_* terminals have their own dedicated transition paths (graph
 * change / Linear cancel webhook) and are deliberately not settable here.
 *
 * @since 1.0.0
 */
export const OrcaTaskTombstonePhase = Schema.Literal("SUPERSEDED", "HOLD");
export type OrcaTaskTombstonePhase = Schema.Schema.Type<typeof OrcaTaskTombstonePhase>;

/**
 * Input schema for tombstoning an orca task into a human-terminal phase.
 *
 * `reason` is recorded in `failure_context` for the operator-readable trail.
 *
 * @since 1.0.0
 */
export const OrcaTaskTombstoneInput = Schema.Struct({
  id: Schema.String,
  phase: OrcaTaskTombstonePhase,
  reason: Schema.String,
});

// ============================================================================
// admin.orcaTask.detail — identifier-keyed read model (INF-844)
// ============================================================================

/**
 * INF-844: input for `admin.orcaTask.detail`. Keyed by the Linear issue
 * identifier (the task-detail route param `/orca/$issueIdentifier`), NOT the
 * task id — the accessor resolves it case-insensitively.
 *
 * @since 1.0.0
 */
export const OrcaTaskDetailInput = Schema.Struct({ issueIdentifier: Schema.String });
export type OrcaTaskDetailInput = Schema.Schema.Type<typeof OrcaTaskDetailInput>;

/**
 * INF-844: the live container the detail page renders, resolved from the
 * assigned runner's Redis container snapshot by the deterministic
 * `orca-{role}-{identifier}-{attempt}` name. `runnerName` is copied from the
 * runner-assignment history event so the operator sees where the container
 * runs. `containerId` is the docker id `admin.orcaRunner.list` already exposes —
 * no secret crosses the wire.
 *
 * @since 1.0.0
 */
export const OrcaTaskLiveContainer = Schema.Struct({
  containerId: Schema.String,
  name: Schema.String,
  image: Schema.String,
  status: Schema.String,
  startedAt: Schema.DateTimeUtc,
  runnerName: Schema.String,
});
export type OrcaTaskLiveContainer = Schema.Schema.Type<typeof OrcaTaskLiveContainer>;

/**
 * INF-844: the single read model behind the orca-admin task-detail page. Carries
 * the full task row (`description` / `failureContext` / `attemptHistory` /
 * `history` intact — unlike the kanban projection), the effective base +
 * integration branch, the assigned runner name, the live container, the last 10
 * events, and the S3-console archive link. Ancillary reads (project row, Redis
 * snapshot) degrade to `null`; only the task row is load-bearing.
 *
 * @since 1.0.0
 */
export const OrcaTaskDetail = Schema.Struct({
  task: OrcaTask.json,
  integrationBranch: Schema.NullOr(Schema.String),
  baseBranch: Schema.String,
  runnerName: Schema.NullOr(Schema.String),
  liveContainer: Schema.NullOr(OrcaTaskLiveContainer),
  recentEvents: Schema.Array(OrcaTaskEvent.json),
  archiveUrl: Schema.String,
});
export type OrcaTaskDetail = Schema.Schema.Type<typeof OrcaTaskDetail>;

/**
 * INF-844: pure wire types for the sequenced rewind follow-up (API-A1R).
 * Defined here so the web seam can re-export every task schema in one pass; the
 * `admin.orcaTask.rewind` RPC itself is NOT registered until that follow-up adds
 * its handler.
 *
 * @since 1.0.0
 */
export const OrcaTaskRewindGate = OrcaTaskGate;
export type OrcaTaskRewindGate = Schema.Schema.Type<typeof OrcaTaskRewindGate>;

/**
 * INF-844: input for the gate-parameterised rewind (API-A1R). `gate` selects
 * which gate to rewind to; `force` (default false) widens the phase allowlist.
 *
 * @since 1.0.0
 */
export const OrcaTaskRewindInput = Schema.Struct({
  id: Schema.String,
  gate: OrcaTaskRewindGate,
  force: Schema.optionalWith(Schema.Boolean, { default: () => false }),
});
export type OrcaTaskRewindInput = Schema.Schema.Type<typeof OrcaTaskRewindInput>;

// ============================================================================
// admin.orcaTask.costSummary — token/cost read model (OL-34)
// ============================================================================

/**
 * OL-34: input for `admin.orcaTask.costSummary`. Keyed by the task id (the same
 * key `findOne` / `kill` / `reset` use), not the Linear identifier.
 *
 * @since 1.0.0
 */
export const OrcaTaskCostSummaryInput = Schema.Struct({ id: Schema.String });
export type OrcaTaskCostSummaryInput = Schema.Schema.Type<typeof OrcaTaskCostSummaryInput>;

/**
 * OL-34: the first reader of `orca_task_token_usage`. Returns every recorded
 * session row for a task alongside their fold — the per-task token totals and
 * the summed USD estimate the orchestrator budgets on.
 *
 * `model` is copied from the TASK row (`orca_tasks.model`), not from the usage
 * table: no model column was added there, and a task's runs all share the
 * dispatched model. It tells the operator which rate card the per-row
 * `estimatedCostUsd` values were derived at (see `model-cost-rates.ts`).
 *
 * `sessions` uses `OrcaTaskTokenUsage.json`, so `createdAt`/`updatedAt` cross
 * the wire as `DateTimeUtc`.
 *
 * @since 1.0.0
 */
export const OrcaTaskCostSummary = Schema.Struct({
  taskId: Schema.String,
  model: Schema.String,
  sessions: Schema.Array(OrcaTaskTokenUsage.json),
  totals: Schema.Struct({
    inputTokens: Schema.Number,
    outputTokens: Schema.Number,
    cacheReadTokens: Schema.Number,
    cacheCreationTokens: Schema.Number,
    estimatedCostUsd: Schema.Number,
    messageCount: Schema.Number,
  }),
});
export type OrcaTaskCostSummary = Schema.Schema.Type<typeof OrcaTaskCostSummary>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Orca Task RPC group
 *
 * All endpoints require admin role via AdminMiddleware.
 *
 * @since 1.0.0
 */
export class AdminOrcaTaskRpcs extends RpcGroup.make(
  Rpc.make("admin.orcaTask.list", {
    success: Schema.Array(OrcaKanbanTaskRow),
    error: Schema.Union(Unauthorized, Forbidden, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.orcaTask.findOne", {
    payload: OrcaTaskFindOneInput,
    success: OrcaTask.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.orcaTask.kill", {
    payload: OrcaTaskKillInput,
    success: Schema.Struct({ success: Schema.Boolean }),
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, OrcaAgentError, DatabaseError),
  }).middleware(AdminMiddleware),
  Rpc.make("admin.orcaTask.reset", {
    payload: OrcaTaskResetInput,
    success: OrcaTask.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  // INF-754: exact CI-gate rewind — restore attempt budget, clear stale gate
  // state, bump dispatch_version, optionally resync the description snapshot and
  // kill a stale container. Refuses terminal phases (and non-terminal
  // BUILDING/MERGING without `force`) via OrcaTaskPhaseNotRewindableError.
  Rpc.make("admin.orcaTask.retry", {
    payload: OrcaTaskRetryInput,
    success: OrcaTaskRetryResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      OrcaTaskPhaseNotRewindableError,
      OrcaAgentError,
      DatabaseError,
    ),
  }).middleware(AdminMiddleware),
  // INF-844: gate-parameterised rewind (ci / completeness / review / merge) for
  // the task-detail "Rewind to gate" action. Built from the CI-retry recipe and
  // the reconciler's gate re-dispatch pair — mutates pipeline state and (for the
  // workflow gates) dispatches a gate workflow, so it is admin-only.
  Rpc.make("admin.orcaTask.rewind", {
    payload: OrcaTaskRewindInput,
    success: OrcaTaskRetryResult,
    error: Schema.Union(
      Unauthorized,
      Forbidden,
      RecordNotFoundError,
      OrcaTaskPhaseNotRewindableError,
      OrcaAgentError,
      DatabaseError,
    ),
  }).middleware(AdminMiddleware),
  // INF-526: flip a non-terminal task into a human-tombstone terminal
  // (SUPERSEDED / HOLD) instead of overloading FAILED. Idempotent — a task
  // already in any terminal phase is rejected with RecordNotFoundError so the
  // operator never silently re-tombstones a finished row.
  Rpc.make("admin.orcaTask.tombstone", {
    payload: OrcaTaskTombstoneInput,
    success: OrcaTask.json,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  // INF-844: identifier-keyed read model for the orca-admin task-detail page.
  // Admin-only — it exposes operator-only internals (failureContext, history,
  // runner assignment, live container id).
  Rpc.make("admin.orcaTask.detail", {
    payload: OrcaTaskDetailInput,
    success: OrcaTaskDetail,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
  // OL-21: server-push counterpart of `detail` for the task-detail page. Emits
  // the current read model immediately, then again only when it changed, so the
  // page paints once and mutates in place instead of flashing on a 5s poll.
  // Same payload/success/error as `detail` — no new wire types.
  Rpc.make("admin.orcaTask.watch", {
    payload: OrcaTaskDetailInput,
    success: OrcaTaskDetail,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
    stream: true,
  }).middleware(AdminMiddleware),
  // OL-34: per-task token/cost read model — the first reader of
  // `orca_task_token_usage`. Admin-only like every sibling: it exposes internal
  // spend the orchestrator budgets on.
  Rpc.make("admin.orcaTask.costSummary", {
    payload: OrcaTaskCostSummaryInput,
    success: OrcaTaskCostSummary,
    error: Schema.Union(Unauthorized, Forbidden, RecordNotFoundError, DatabaseError),
  }).middleware(AdminMiddleware),
) {}
